/**
 * PublicMembers — /club/members. Anonymous, read-only.
 *
 * The club roster, current players and ex-members shown separately. Tapping a
 * name opens that player's public stats page (the member PlayerProfilePage in
 * publicMode), so the directory is a way into the stats rather than a dead end.
 *
 * Styled to match the member leaderboard rows — 40px gradient avatar,
 * Rajdhani-800 name, muted sub-line — so it belongs to the same app.
 *
 * The API returns names, photos and headline figures only. No contact details.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

const NAVY = '#1E3A6E';
const NAVY_LIGHT = '#2d5699';
const GOLD = '#F5C518';

function Row({ m, former }) {
  return (
    <Link to={`/club/stats/${m.id}`} style={{ textDecoration: 'none', display: 'block' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12,
        padding: '12px 14px', marginBottom: 2,
        background: 'white', borderRadius: 12,
        border: '1px solid #f1f5f9',
        borderLeft: `4px solid ${former ? '#cbd5e1' : NAVY}`,
      }}>
        <div style={{
          width: 40, height: 40, borderRadius: '50%', flexShrink: 0, overflow: 'hidden',
          background: `linear-gradient(135deg, ${NAVY}, ${NAVY_LIGHT})`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: 'white', fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem',
          filter: former ? 'grayscale(0.6)' : 'none',
        }}>
          {m.photo_url
            ? <img src={m.photo_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : m.display_name?.[0]?.toUpperCase()}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontFamily: 'Rajdhani,sans-serif', fontWeight: 800, fontSize: '1rem',
            color: former ? '#64748b' : NAVY,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {m.display_name}
            {m.jersey_no ? <span style={{ color: '#9ca3af', fontWeight: 700, fontSize: '0.8rem' }}> · #{m.jersey_no}</span> : null}
          </div>
          <div style={{ fontSize: '0.7rem', color: '#6b7280' }}>
            {m.matches} {m.matches === 1 ? 'match' : 'matches'} · {m.runs} runs · {m.wickets} wkts
          </div>
        </div>

        <span style={{ color: '#cbd5e1', fontSize: '1.1rem' }}>›</span>
      </div>
    </Link>
  );
}

export default function PublicMembers() {
  const [data, setData] = useState({ active: [], former: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('active');

  useEffect(() => {
    let alive = true;
    fetch('/api/public/members')
      .then(r => r.json())
      .then(d => { if (alive) setData({ active: d.active || [], former: d.former || [] }); })
      .catch(() => { if (alive) setErr('Members are unavailable right now. Reload in a moment.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const list = tab === 'active' ? data.active : data.former;

  return (
    <>
      <div style={{ fontFamily: 'Rajdhani,sans-serif', fontWeight: 800,
        fontSize: '1.9rem', color: NAVY, marginBottom: 3 }}>Members</div>
      <div style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: 16 }}>
        Tap any name for their full stats.
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[['active', `Current Members (${data.active.length})`],
          ['former', `Ex-Members (${data.former.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{
            padding: '7px 16px', borderRadius: 20, cursor: 'pointer',
            border: tab === k ? `1.5px solid ${NAVY}` : '1.5px solid #e5e7eb',
            background: tab === k ? NAVY : 'white',
            color: tab === k ? 'white' : '#6b7280',
            fontSize: '0.8rem', fontWeight: 700, fontFamily: 'inherit',
          }}>{label}</button>
        ))}
      </div>

      {err && (
        <div style={{ background: '#fff1f2', border: '1px solid #fca5a5', color: '#991b1b',
          borderRadius: 12, padding: 12, fontSize: '0.82rem' }}>{err}</div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>Loading...</div>
      ) : list.length === 0 && !err ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#9ca3af', fontSize: '0.88rem' }}>
          {tab === 'active' ? 'No members listed yet.' : 'No ex-members recorded.'}
        </div>
      ) : (
        <>
          {tab === 'former' && (
            <div style={{
              background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 10,
              padding: '10px 13px', marginBottom: 10, fontSize: '0.76rem', color: '#64748b',
            }}>
              Players who've moved on — their records stay part of the club's history.
            </div>
          )}
          {list.map(m => <Row key={m.id} m={m} former={tab === 'former'} />)}
        </>
      )}
    </>
  );
}
